import { Component, ErrorHandler, Inject, OnInit, ChangeDetectionStrategy, signal } from '@angular/core'
import { FieldTree, form } from '@angular/forms/signals'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { TiedostojenLataamisService } from 'app/_jaettu-angular/service/tiedostojen-lataamis.service'
import { TimestampService } from 'app/_jaettu-angular/service/timestamp-service'
import { Asiakas } from 'app/_jaettu-lemonator/model/asiakas'
import { ArkistoFile, ArkistoFileType, ArkistoService } from 'app/_jaettu-lemonator/service/arkisto.service'
import { EnvironmentType } from 'app/app.environment'
import { environment } from 'environments/environment'
import { BehaviorSubject, Subject, firstValueFrom, takeUntil } from 'rxjs'
import { FileSystemFileEntry } from '../../_jaettu-angular/file-drop/file-drop-dom.types'
import { FileDropEntry } from '../../_jaettu-angular/file-drop/file-drop-entry'

export interface ArkistoSisainenDialogData {
  asiakas: Asiakas
  type: ArkistoFileType
}

interface ArkistotiedostoLomake {
  lisatiedot: string
}

@Component({
  templateUrl: './arkisto-sisainen-upload.dialog.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ArkistoSisainenUploadDialog implements OnInit {
  private _ngUnsubscribe = new Subject<void>()

  lomake: FieldTree<ArkistotiedostoLomake> = form(signal<ArkistotiedostoLomake>({ lisatiedot: '' }))
  latausVirheObservable = new BehaviorSubject<string>(null)
  private _fileLoadingSubject = new BehaviorSubject<boolean>(false)
  fileLoadingObservable = this._fileLoadingSubject.asObservable().pipe(takeUntil(this._ngUnsubscribe))
  private _saveStarted = false

  readonly uploadedFile = signal<{ avain: string; fileName: string }>(undefined)

  constructor(
    @Inject(MAT_DIALOG_DATA) private _data: ArkistoSisainenDialogData,
    private _dialogRef: MatDialogRef<ArkistoSisainenUploadDialog>,
    private _tiedostojenLataamisService: TiedostojenLataamisService,
    private _ladataanService: LadataanService,
    private _timestampService: TimestampService,
    private _lemonatorFirebase: FirebaseLemonator,
    private _arkistoService: ArkistoService,
    private _errorHandler: ErrorHandler,
    private _formValidationService: FormValidationService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
  ) {
    _dialogRef.disableClose = true
  }

  ngOnInit() {}

  async upload(event: Event) {
    this.latausVirheObservable.next(null)
    this._fileLoadingSubject.next(true)
    this._ladataanService.aloitaLataaminen()

    const inputTypeFile = event?.target as HTMLInputElement
    if (inputTypeFile?.files) {
      const list: FileList = inputTypeFile.files
      const tiedostot: FileDropEntry[] = this._tiedostojenLataamisService.fileListToNgxFileDropEntries(list)
      try {
        await this._uploadFile(tiedostot)
        this._fileLoadingSubject.next(false)
        this._ladataanService.lopetaLataaminen()
      } catch (err) {
        this._ladataanService.lopetaLataaminen()
        this._fileLoadingSubject.next(false)
        this.latausVirheObservable.next('Lataamisen aikana tapahtui virhe: ' + err)
      }
    }
  }

  private async _uploadFile(tiedostot: FileDropEntry[]) {
    this._ladataanService.aloitaLataaminen()

    for (const tiedosto of tiedostot) {
      const file = await this._tiedostojenLataamisService.getFile(tiedosto.fileEntry as FileSystemFileEntry)
      const avain = this._lemonatorFirebase.firestoreCreateId()

      this.uploadedFile.set({ avain, fileName: file.name })

      const bucket = environment.environment === EnvironmentType.PRODUCTION ? 'arkisto-internal' : 'arkisto-internal-test'
      const storageUri = this._arkistoService.getArkistoInternalFileStorageUri(this._data.asiakas.avain, { avain })

      const runningUpload = this._tiedostojenLataamisService.upload(this._lemonatorFirebase, storageUri, file, bucket)
      await firstValueFrom(runningUpload.doneObservable)
    }
  }

  async save() {
    if (!this.lomake().valid()) {
      this._formValidationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    if (this._saveStarted) {
      return
    }
    this._saveStarted = true

    this._ladataanService.aloitaLataaminen()

    try {
      const kirjanpitaja = await this._kirjautunutKayttajaService.getKirjanpitajanTiedot()
      const asiakasAvain = this._data.asiakas.avain
      const metadataAvain = this.uploadedFile().avain
      const lisatiedot = this.lomake.lisatiedot()

      const metadata: ArkistoFile = {
        avain: metadataAvain,
        fileName: this.uploadedFile().fileName,
        asiakasAvain,
        // Kenttään kirjoittamaton lisätieto tallennetaan null-arvona
        lisatiedot: lisatiedot.dirty() ? lisatiedot.value() : null,
        poistettu: false,
        paivitetty: this._timestampService.now(),
        luotu: this._timestampService.now(),
        paivittaja: kirjanpitaja.uid,
        luoja: kirjanpitaja.uid,
        type: this._data.type,
        tilikaudet: null,
        tiedostoTyyppi: null,
      }

      const uri = this._arkistoService.getArkistoMetadataUri(asiakasAvain, metadataAvain)
      await this._lemonatorFirebase.firestoreSetData(uri, metadata)

      this._dialogRef.close()
    } catch (err) {
      this._errorHandler.handleError(err)
    } finally {
      this._saveStarted = false
      this._ladataanService.lopetaLataaminen()
    }
  }
}
